/**
 * PWS × AWARE — First-login Partner Welcome page
 *
 * Route: /partner/welcome
 *
 * Shown once to a franchisee immediately after they accept the formal
 * Trade Portal invite and set their password. From here they can jump
 * to the Partner Dashboard, review test packages, or open the digital
 * Partner Guide (which stamps `partner_guide_opened_at` server-side).
 *
 * How "shown once" works:
 *   • On mount we check the linked partner row's `partner_guide_opened_at`.
 *   • If already set AND the URL doesn't force it via `?force=1`,
 *     we redirect to the Partner Dashboard so long-term users aren't
 *     forced back through the welcome mat on every login.
 *   • The Partner Resources sidebar continues to link here for
 *     franchisees who want to revisit the guide later.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  BookOpen, Download, LayoutDashboard, Package, ArrowRight,
  Sparkles, ExternalLink,
} from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { BrandLogo } from "@/components/BrandLogo";

export default function PartnerWelcome() {
  useSEO({
    title: "Welcome — AWARE Partner Network",
    noindex: true,
    includeBaseline: false,
  });
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const force = search.get("force") === "1";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/pws-partner/config");
        if (cancelled) return;
        setPdfUrl(data?.pdf_url || null);
      } catch {
        /* silent — non-fatal */
      }
      try {
        // Ask the partner-dashboard endpoint for the guide-opened stamp.
        const { data } = await api.get("/partner/dashboard/summary");
        const openedAt = data?.partner_guide_opened_at || data?.partner?.partner_guide_opened_at;
        if (openedAt && !force) {
          navigate("/partner/dashboard", { replace: true });
          return;
        }
      } catch {
        /* fall through — always render if we can't tell */
      }
      if (!cancelled) setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [force, navigate]);

  async function stampGuideOpened() {
    try { await api.post("/partner/pws-partner-guide-opened"); }
    catch { /* fire and forget */ }
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading your Partner Portal…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#031533] via-[#062754] to-[#0a3878] text-white">
      <div className="mx-auto max-w-4xl px-6 pt-14 pb-20" data-testid="partner-welcome-page">
        <BrandLogo className="h-9 w-auto" />

        <div className="mt-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-3 py-1 text-xs uppercase tracking-[0.14em] text-cyan-100">
            <Sparkles className="h-3.5 w-3.5" /> PWS × AWARE Partner Network
          </div>
          <h1
            data-testid="partner-welcome-heading"
            className="mt-6 text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight"
          >
            Welcome to the AWARE Partner Network
          </h1>
          <p className="mt-5 text-lg text-cyan-50/85 leading-relaxed max-w-2xl">
            Your Partner Portal account is ready. Take a quick tour of the
            digital Partner Guide, then jump into the Dashboard to see your
            bookings, results and marketing tools.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          <a
            href="/pws-partner/guide/index.html"
            target="_blank"
            rel="noopener noreferrer"
            onClick={stampGuideOpened}
            data-testid="partner-welcome-guide"
            className="group rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-6 hover:bg-white/10 transition-colors"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-400 text-slate-900">
              <BookOpen className="h-5 w-5" />
            </div>
            <div className="mt-4 text-lg font-semibold">Open the Digital Partner Guide</div>
            <p className="mt-1 text-sm text-cyan-100/80">
              Everything you need to explain and manage AWARE testing across
              your franchise.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-cyan-300 text-sm font-medium">
              Open guide <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </div>
          </a>

          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank" rel="noopener noreferrer"
              data-testid="partner-welcome-pdf"
              className="group rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-6 hover:bg-white/10 transition-colors"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-900">
                <Download className="h-5 w-5" />
              </div>
              <div className="mt-4 text-lg font-semibold">Download PDF</div>
              <p className="mt-1 text-sm text-cyan-100/80">
                Prefer offline? Grab the compressed A4 Partner Guide.
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-cyan-300 text-sm font-medium">
                Download <ExternalLink className="h-3.5 w-3.5" />
              </div>
            </a>
          )}

          <Link
            to="/book-water-test"
            data-testid="partner-welcome-packages"
            className="group rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-6 hover:bg-white/10 transition-colors"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-400/20 text-cyan-200">
              <Package className="h-5 w-5" />
            </div>
            <div className="mt-4 text-lg font-semibold">Review AWARE Test Packages</div>
            <p className="mt-1 text-sm text-cyan-100/80">
              See the 4-Point, Tank 360 and Bore 360 packages the way your
              customers do.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-cyan-300 text-sm font-medium">
              View packages <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>

          <Link
            to="/partner/dashboard"
            data-testid="partner-welcome-dashboard"
            className="group rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-6 hover:bg-white/10 transition-colors"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-400 text-slate-900">
              <LayoutDashboard className="h-5 w-5" />
            </div>
            <div className="mt-4 text-lg font-semibold">Go to Partner Dashboard</div>
            <p className="mt-1 text-sm text-cyan-100/80">
              Bookings, results, referrals and marketing tools in one place.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-cyan-300 text-sm font-medium">
              Open dashboard <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        </div>

        <p className="mt-12 text-xs text-cyan-100/60">
          Better testing. Stronger partnerships. Healthier water for all.
        </p>
      </div>
    </div>
  );
}
